import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, MapPin, Truck, ArrowRight, ArrowLeft, Lock } from "lucide-react";
import Layout from "../components/Layout";
import StepIndicator from "../components/StepIndicator";
import BakeryBox from "../components/BakeryBox";
import CookieImage from "../components/CookieImage";
import { useAuth } from "../context/AuthContext";
import { useOrder } from "../context/OrderContext";
import { api, errorMessage } from "../lib/api";

const DEFAULT_BOX_OPTIONS = [
  { size: 1, price: 5 },
  { size: 2, price: 8 },
  { size: 4, price: 15 },
  { size: 6, price: 25 },
  { size: 12, price: 40 },
];

export default function OrderPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const order = useOrder();
  const [step, setStep] = useState(1);
  const [flavors, setFlavors] = useState([]);
  const [settings, setSettings] = useState({ orders_open: true });
  const [submitting, setSubmitting] = useState(false);
  const [points, setPoints] = useState(user?.points || 0);
  const [redeem, setRedeem] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // { code, kind, discount, label }
  const [promoLoading, setPromoLoading] = useState(false);

  // Auto-scroll to top whenever the step changes (mobile-friendly, fires after render)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [step]);

  useEffect(() => {
    if (user && user.role !== "admin") {
      api.get("/rewards/me").then(r => setPoints(r.data.points || 0)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!loading && !user) {
      nav(`/login?redirect=${encodeURIComponent("/order")}`);
    }
  }, [user, loading, nav]);

  useEffect(() => {
    api.get("/flavors").then(r => setFlavors(r.data)).catch(() => {});
    api.get("/settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const BOX_OPTIONS = useMemo(() => {
    const bp = settings.box_prices;
    if (!bp) return DEFAULT_BOX_OPTIONS;
    return DEFAULT_BOX_OPTIONS.map(b => ({ size: b.size, price: parseFloat(bp[String(b.size)] ?? b.price) }));
  }, [settings]);

  const totalSelected = useMemo(() => Object.values(order.items || {}).reduce((a, b) => a + b, 0), [order.items]);

  const picks = useMemo(() => {
    const arr = [];
    for (const [fid, q] of Object.entries(order.items || {})) {
      const f = flavors.find(x => x.id === fid);
      if (!f) continue;
      for (let i = 0; i < q; i++) arr.push({ flavor_id: fid, image_key: f.image_key, image_url: f.image_url });
    }
    return arr;
  }, [order.items, flavors]);

  const subtotal = order.boxSize ? BOX_OPTIONS.find(b => b.size === order.boxSize)?.price || 0 : 0;
  const deliveryFee = order.method === "delivery" ? (order.delivery?.fee || 0) : 0;
  const tipAmount = useMemo(() => {
    if (order.tipPercent === "custom") return Math.max(0, parseFloat(order.tipCustom || "0") || 0);
    return +(subtotal * (order.tipPercent / 100)).toFixed(2);
  }, [order.tipPercent, order.tipCustom, subtotal]);
  const total = +(subtotal + deliveryFee + tipAmount).toFixed(2);

  const setQty = (fid, delta) => {
    const current = order.items[fid] || 0;
    const next = Math.max(0, current + delta);
    const others = totalSelected - current;
    if (others + next > order.boxSize) {
      toast.error(`Your box only holds ${order.boxSize} cookies`);
      return;
    }
    const newItems = { ...order.items };
    if (next === 0) delete newItems[fid];
    else newItems[fid] = next;
    order.update({ items: newItems });
  };

  const goNext = () => {
    if (step === 1 && !order.boxSize) { toast.error("Choose a box size"); return; }
    if (step === 2 && totalSelected !== order.boxSize) { toast.error(`Fill all ${order.boxSize} cookie spots`); return; }
    if (step === 3 && !order.method) { toast.error("Pick pickup or delivery"); return; }
    if (step === 3 && order.method === "delivery") {
      if (!order.address.street || !order.address.city || !order.address.zip) {
        toast.error("Enter delivery address"); return;
      }
      if (!order.delivery?.checked) {
        toast.error("Please tap CHECK ADDRESS to verify delivery"); return;
      }
      if (!order.delivery?.eligible) {
        toast.error("This address is outside our delivery zone — please choose pickup"); return;
      }
    }
    setStep(s => Math.min(5, s + 1));
  };
  const goBack = () => { setStep(s => Math.max(1, s - 1)); };

  const submitOrder = async () => {
    setSubmitting(true);
    try {
      const items = Object.entries(order.items).map(([flavor_id, quantity]) => ({ flavor_id, quantity }));
      const body = {
        box_size: order.boxSize,
        items,
        method: order.method,
        address_street: order.address.street || "",
        address_city: order.address.city || "",
        address_state: order.address.state || "",
        address_zip: order.address.zip || "",
        tip_amount: tipAmount,
        redeem_free_cookie: redeem && points >= 500,
        promo_code: promo?.code || "",
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/orders/checkout", body);
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errorMessage(e)); setSubmitting(false); }
  };

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    try {
      const itemCount = Object.values(order.items).reduce((a, b) => a + b, 0);
      const { data } = await api.post("/promo/apply", { code, subtotal, item_count: itemCount });
      setPromo(data);
      toast.success(`Code applied: ${data.label}`);
    } catch (e) { toast.error(errorMessage(e)); setPromo(null); }
    finally { setPromoLoading(false); }
  };

  const removePromo = () => { setPromo(null); setPromoInput(""); };

  if (loading || !user) {
    return <Layout><div className="step-shell"><p className="text-center mt-20 text-white">Loading...</p></div></Layout>;
  }

  if (!settings.orders_open) {
    return (
      <Layout>
        <div className="step-shell">
          <div className="max-w-2xl mx-auto px-4 text-center" data-testid="orders-closed">
            <h1 className="font-display font-extrabold text-3xl md:text-5xl text-white drop-shadow-lg">Orders Are Closed</h1>
            <p className="mt-4 text-white/90">Check back soon — new orders open weekly!</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="step-shell">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <StepIndicator current={step} />

          {step === 1 && (
            <div className="fade-in" data-testid="order-step-1">
              <div className="text-center mb-10">
                <p className="text-sm uppercase tracking-[0.25em] font-bold text-white/85 drop-shadow">Step 1</p>
                <h2 className="font-display font-extrabold text-3xl md:text-5xl mt-2 text-white drop-shadow-lg">Choose Your Box Size</h2>
                <p className="text-white/90 mt-3">Each cookie is approximately 4 oz and 4 inches large.</p>
              </div>
              <div className="grid gap-4 max-w-2xl mx-auto">
                {BOX_OPTIONS.map(b => {
                  const active = order.boxSize === b.size;
                  return (
                    <button
                      key={b.size}
                      onClick={() => { order.update({ boxSize: b.size, items: {} }); }}
                      className={`card-pressable text-left w-full p-6 rounded-3xl border-2 bg-white ${active ? "is-selected border-[#FF1F87]" : "border-gray-100 hover:border-[#FF5A7E]"}`}
                      data-testid={`box-size-${b.size}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-display font-extrabold text-xl md:text-2xl">{b.size} {b.size === 1 ? "Cookie" : "Cookies"}</p>
                          <p className="text-sm text-[#4B5563] mt-1">{b.size === 1 ? "Perfect treat for one" : `Share with ${b.size - 1}+`}</p>
                        </div>
                        <p className="font-display font-extrabold text-2xl md:text-3xl text-[#FF1F87]">${b.price}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="fade-in" data-testid="order-step-2">
              <div className="text-center mb-8">
                <p className="text-sm uppercase tracking-[0.25em] font-bold text-white/85 drop-shadow">Step 2</p>
                <h2 className="font-display font-extrabold text-3xl md:text-5xl mt-2 text-white drop-shadow-lg">Build Your Box</h2>
                <p className="text-white/90 mt-2">Select {order.boxSize} cookies</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 space-y-4">
                  {flavors.map(f => {
                    const q = order.items[f.id] || 0;
                    return (
                      <div key={f.id} className="card-soft p-4 md:p-5 flex items-center gap-4" data-testid={`flavor-row-${f.image_key}`}>
                        <CookieImage flavorKey={f.image_key} imageUrl={f.image_url} className="w-20 h-20 md:w-24 md:h-24 rounded-full flex-shrink-0 shadow-md" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display font-bold text-base md:text-lg leading-tight">{f.name}</h3>
                          <p className="text-xs md:text-sm text-[#4B5563] mt-1 line-clamp-2">{f.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setQty(f.id, -1)} className="w-9 h-9 rounded-full border-2 border-[#FF1F87] text-[#FF1F87] flex items-center justify-center disabled:opacity-40" disabled={q === 0} data-testid={`flavor-minus-${f.image_key}`}>
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="font-display font-extrabold text-lg w-6 text-center" data-testid={`flavor-qty-${f.image_key}`}>{q}</span>
                          <button onClick={() => setQty(f.id, 1)} className="w-9 h-9 rounded-full bg-[#FF1F87] text-white flex items-center justify-center disabled:opacity-40" disabled={totalSelected >= order.boxSize} data-testid={`flavor-plus-${f.image_key}`}>
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="lg:col-span-2 lg:sticky lg:top-28 h-fit">
                  <BakeryBox boxSize={order.boxSize} picks={picks} total={totalSelected} />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="fade-in" data-testid="order-step-3">
              <div className="text-center mb-10">
                <p className="text-sm uppercase tracking-[0.25em] font-bold text-white/85 drop-shadow">Step 3</p>
                <h2 className="font-display font-extrabold text-3xl md:text-5xl mt-2 text-white drop-shadow-lg">How Would You Like Your Cookies?</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                <button onClick={() => order.update({ method: "pickup" })} className={`card-pressable text-left p-6 rounded-3xl border-2 bg-white ${order.method === "pickup" ? "is-selected border-[#FF1F87]" : "border-gray-100 hover:border-[#FF5A7E]"}`} data-testid="method-pickup">
                  <MapPin className="w-8 h-8 text-[#FF1F87] mb-3" />
                  <h3 className="font-display font-extrabold text-xl">Local Pickup</h3>
                  <p className="text-sm text-[#4B5563] mt-1">Rock Hill / York County Area</p>
                  <p className="text-xs text-[#4B5563] mt-3">Exact pickup details provided after ordering.</p>
                </button>
                <button onClick={() => order.update({ method: "delivery" })} className={`card-pressable text-left p-6 rounded-3xl border-2 bg-white ${order.method === "delivery" ? "is-selected border-[#FF1F87]" : "border-gray-100 hover:border-[#FF5A7E]"}`} data-testid="method-delivery">
                  <Truck className="w-8 h-8 text-[#FF1F87] mb-3" />
                  <h3 className="font-display font-extrabold text-xl">Local Delivery</h3>
                  <p className="text-xs text-[#4B5563] mt-3">Check eligibility</p>
                </button>
              </div>

              {order.method === "delivery" && (
                <div className="max-w-2xl mx-auto mt-8 card-soft p-6 fade-in">
                  <h4 className="font-display font-bold text-lg mb-1">Delivery Address</h4>
                  <p className="text-xs text-[#4B5563] mb-4">Enter your address to check eligibility. $1.50/mile.</p>
                  <div className="space-y-3">
                    <input className="input-base" placeholder="Street Address" value={order.address.street} onChange={e => order.update({ address: { ...order.address, street: e.target.value }, delivery: { checked: false, eligible: false, distance: 0, fee: 0, message: "" } })} data-testid="address-street" />
                    <div className="grid grid-cols-3 gap-3">
                      <input className="input-base col-span-2" placeholder="City" value={order.address.city} onChange={e => order.update({ address: { ...order.address, city: e.target.value }, delivery: { checked: false, eligible: false, distance: 0, fee: 0, message: "" } })} data-testid="address-city" />
                      <input className="input-base" placeholder="State" maxLength={2} value={order.address.state} onChange={e => order.update({ address: { ...order.address, state: e.target.value.toUpperCase() }, delivery: { checked: false, eligible: false, distance: 0, fee: 0, message: "" } })} data-testid="address-state" />
                    </div>
                    <input className="input-base" placeholder="ZIP Code" maxLength={10} value={order.address.zip} onChange={e => order.update({ address: { ...order.address, zip: e.target.value }, delivery: { checked: false, eligible: false, distance: 0, fee: 0, message: "" } })} data-testid="address-zip" />
                    <button
                      type="button"
                      onClick={async () => {
                        const a = order.address;
                        if (!a.street || !a.city || !a.zip) { toast.error("Fill street, city, and ZIP first"); return; }
                        order.update({ delivery: { checked: false, eligible: false, distance: 0, fee: 0, message: "Checking..." } });
                        try {
                          const { data } = await api.post("/delivery/check", { street: a.street, city: a.city, state: a.state || "SC", zip: a.zip });
                          order.update({ delivery: { checked: true, eligible: !!data.eligible, distance: data.distance_miles || 0, fee: data.delivery_fee || 0, message: data.message || "" } });
                        } catch (e) {
                          order.update({ delivery: { checked: true, eligible: false, distance: 0, fee: 0, message: errorMessage(e) } });
                        }
                      }}
                      className="btn-pink w-full"
                      data-testid="check-address-btn"
                    >
                      CHECK ADDRESS
                    </button>

                    {order.delivery?.checked && (
                      <div
                        className={`mt-3 p-4 rounded-2xl text-sm font-semibold ${order.delivery.eligible ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-rose-50 text-rose-800 border border-rose-200"}`}
                        data-testid="delivery-check-result"
                      >
                        {order.delivery.eligible ? (
                          <>
                            <p className="font-display font-extrabold text-base">✓ Delivery Available</p>
                            <p className="mt-1">Distance: {order.delivery.distance} miles</p>
                            <p>Delivery Fee: ${order.delivery.fee.toFixed(2)}</p>
                          </>
                        ) : (
                          <p>{order.delivery.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="fade-in" data-testid="order-step-4">
              <div className="text-center mb-10">
                <p className="text-sm uppercase tracking-[0.25em] font-bold text-white/85 drop-shadow">Step 4</p>
                <h2 className="font-display font-extrabold text-3xl md:text-5xl mt-2 text-white drop-shadow-lg">Add A Tip</h2>
                <p className="text-white/90 mt-2">100% of tips go to our cookie crew</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 max-w-2xl mx-auto mb-6">
                {[0, 5, 10, 20].map(p => {
                  const active = order.tipPercent === p;
                  const label = p === 0 ? "NO TIP" : `${p}%`;
                  return (
                    <button key={p} onClick={() => order.update({ tipPercent: p })} className={`card-pressable p-5 rounded-3xl border-2 bg-white ${active ? "is-selected border-[#FF1F87]" : "border-gray-100"}`} data-testid={`tip-${p}`}>
                      <p className="font-display font-extrabold text-2xl">{label}</p>
                      <p className="text-xs text-[#4B5563] mt-1">${(subtotal * p / 100).toFixed(2)}</p>
                    </button>
                  );
                })}
                <div className={`p-3 rounded-3xl border-2 bg-white transition-all ${order.tipPercent === "custom" ? "border-[#FF1F87] ring-4 ring-[#FF1F87]/15" : "border-gray-100"}`}>
                  <p className="font-display font-extrabold text-sm text-center mb-1">CUSTOM</p>
                  <input
                    type="number" min="0" step="0.01"
                    className="input-base !p-2 text-center"
                    placeholder="$"
                    value={order.tipCustom}
                    onChange={(e) => order.update({ tipPercent: "custom", tipCustom: e.target.value })}
                    data-testid="tip-custom"
                  />
                </div>
              </div>

              <div className="max-w-md mx-auto card-soft p-6 space-y-2 text-sm">
                <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
                <Row label="Delivery Fee" value={`$${deliveryFee.toFixed(2)}`} />
                <Row label={`Tip${order.tipPercent !== "custom" ? ` (${order.tipPercent}%)` : ""}`} value={`$${tipAmount.toFixed(2)}`} />
                <div className="border-t border-gray-200 pt-2 mt-2 flex items-center justify-between font-display font-extrabold text-xl">
                  <span>TOTAL</span><span data-testid="order-step4-total">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="fade-in" data-testid="order-step-5">
              <div className="text-center mb-10">
                <p className="text-sm uppercase tracking-[0.25em] font-bold text-white/85 drop-shadow">Step 5</p>
                <h2 className="font-display font-extrabold text-3xl md:text-5xl mt-2 text-white drop-shadow-lg">Review Your Order</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <div className="card-soft p-6 space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#FF1F87] font-bold">Box Size</p>
                    <p className="font-display font-bold text-lg">{order.boxSize} Cookies</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#FF1F87] font-bold">Flavors</p>
                    <ul className="text-sm mt-1 space-y-1">
                      {Object.entries(order.items).map(([fid, q]) => {
                        const f = flavors.find(x => x.id === fid);
                        return <li key={fid}>{q}× {f?.name}</li>;
                      })}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#FF1F87] font-bold">Method</p>
                    <p className="text-sm">{order.method === "pickup" ? "Local Pickup" : "Local Delivery"}</p>
                    {order.method === "delivery" && (
                      <p className="text-sm text-[#4B5563] mt-1">{order.address.street}, {order.address.city}, {order.address.zip}</p>
                    )}
                  </div>
                </div>

                <div className="card-soft p-6 space-y-2">
                  <p className="text-xs uppercase tracking-widest text-[#FF1F87] font-bold mb-3">Summary</p>
                  {(() => {
                    const rewardEligible = redeem && points >= 500;
                    const promoOff = promo?.kind === "percent_off" ? +(promo.discount || 0) : 0;
                    const baseTotal = +(subtotal + deliveryFee + tipAmount - promoOff).toFixed(2);
                    const maxRewardDiscount = Math.max(0, baseTotal - 0.50);
                    const rewardDiscount = rewardEligible ? +Math.min(5, maxRewardDiscount).toFixed(2) : 0;
                    const finalTotal = +(baseTotal - rewardDiscount).toFixed(2);
                    return (
                      <>
                        <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
                        <Row label="Delivery Fee" value={`$${deliveryFee.toFixed(2)}`} />
                        <Row label="Tip" value={`$${tipAmount.toFixed(2)}`} />
                        {promoOff > 0 && (
                          <Row label={`Promo (${promo.code})`} value={`-$${promoOff.toFixed(2)}`} />
                        )}
                        {promo?.kind === "bonus_points" && (
                          <Row label={`Promo (${promo.code})`} value={promo.label} />
                        )}
                        {rewardDiscount > 0 && (
                          <Row label="Free Cookie Reward" value={`-$${rewardDiscount.toFixed(2)}`} />
                        )}
                        <div className="border-t border-gray-200 pt-2 mt-2 flex items-center justify-between font-display font-extrabold text-2xl">
                          <span>TOTAL</span><span className="text-[#FF1F87]" data-testid="order-step5-total">${finalTotal.toFixed(2)}</span>
                        </div>
                      </>
                    );
                  })()}

                  {/* Promo code input */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    {!promo ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          placeholder="PROMO CODE"
                          className="input-base !py-2 uppercase tracking-widest text-sm"
                          data-testid="promo-input"
                        />
                        <button
                          type="button"
                          onClick={applyPromo}
                          disabled={!promoInput.trim() || promoLoading}
                          className="px-4 py-2 rounded-2xl font-bold text-sm bg-[#FF1F87] text-white disabled:opacity-50"
                          data-testid="promo-apply-btn"
                        >
                          {promoLoading ? "..." : "APPLY"}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between bg-[#FFE4EE] rounded-2xl px-3 py-2" data-testid="promo-applied">
                        <p className="text-sm font-bold text-[#FF1F87]">{promo.code} — {promo.label}</p>
                        <button type="button" onClick={removePromo} className="text-xs text-[#FF1F87] underline">Remove</button>
                      </div>
                    )}
                  </div>

                  {points >= 500 && (
                    <button
                      type="button"
                      onClick={() => setRedeem(r => !r)}
                      className={`w-full mt-3 py-3 rounded-full font-bold text-sm transition-colors ${redeem ? "bg-[#FFE4EE] text-[#FF1F87] border-2 border-[#FF1F87]" : "bg-[#FFF8F2] text-[#111827] border-2 border-dashed border-[#FF5A7E]"}`}
                      data-testid="redeem-reward-btn"
                    >
                      {redeem ? "✓ Free cookie reward applied" : `🎉 Redeem 500 pts for a free cookie (you have ${points})`}
                    </button>
                  )}

                  <button onClick={submitOrder} disabled={submitting} className="btn-pink w-full mt-4" data-testid="proceed-to-payment-btn">
                    <Lock className="w-4 h-4 mr-2" /> {submitting ? "REDIRECTING..." : "PROCEED TO PAYMENT"}
                  </button>
                  <p className="text-xs text-[#4B5563] text-center mt-1">Secure checkout powered by Stripe</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center mt-12 max-w-3xl mx-auto">
            {step > 1 ? (
              <button onClick={goBack} className="btn-outline-white !py-2 !px-5 text-sm" data-testid="order-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> BACK
              </button>
            ) : <div />}
            {step < 5 && (
              <button onClick={goNext} className="btn-white !py-3 !px-7" data-testid="order-next-btn">
                CONTINUE <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#4B5563]">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
